import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { listenToProviderBookings } from '../services/bookingService';
import DashboardHero from '../components/DashboardHero';
import JobRequestCard from '../components/JobRequestCard';
import { FaClock, FaCheckCircle, FaHardHat, FaTrophy, FaClipboardList, FaChartLine, FaUserCircle, FaSyncAlt, FaBriefcase, FaHourglassHalf } from 'react-icons/fa';
import './DashboardPage.css';

const DashboardPage = () => {
    const { currentUser, getProviderProfile } = useAuth();
    const [recentJobs, setRecentJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState(null);
    const [stats, setStats] = useState({ pending: 0, accepted: 0, inProgress: 0, completed: 0 });
    const isApprovedWorker = !!(profile?.isVerified || profile?.isActive || profile?.status === 'approved');

    // Fetch provider profile to check verification status
    useEffect(() => {
        if (!currentUser) {
            setLoading(false);
            return;
        }

        const fetchProfile = async () => {
            try {
                const p = await getProviderProfile(currentUser.uid);
                setProfile(p);
            } catch (err) {
                console.error('Error fetching profile:', err);
            }
        };
        fetchProfile();
    }, [currentUser, getProviderProfile]);

    useEffect(() => {
        if (!currentUser || !navigator.geolocation) return;
        if (!isApprovedWorker) return;

        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    await updateDoc(doc(db, 'serviceProviders', currentUser.uid), {
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude,
                        locationUpdatedAt: new Date().toISOString(),
                    });
                } catch (e) { /* silent */ }
            },
            () => { },
            { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
        );
    }, [currentUser, isApprovedWorker]);

    useEffect(() => {
        if (!currentUser) {
            setLoading(false);
            return;
        }
        if (!isApprovedWorker) {
            setLoading(false);
            return;
        }

        let unsubscribe = null;

        const setup = async () => {
            try {
                const serviceCategory = profile?.serviceCategory || 'electrician';

                unsubscribe = listenToProviderBookings(
                    currentUser.uid,
                    serviceCategory,
                    (bookings) => {
                        setRecentJobs(bookings.slice(0, 3));
                        setStats({
                            pending: bookings.filter(b => b.status === 'pending').length,
                            accepted: bookings.filter(b => b.status === 'accepted').length,
                            inProgress: bookings.filter(b => b.status === 'in-progress').length,
                            completed: bookings.filter(b => b.status === 'completed').length
                        });
                        setLoading(false);
                    }
                );
            } catch (err) {
                console.error('Dashboard setup failed:', err);
                setLoading(false);
            }
        };

        setup();
        return () => { if (unsubscribe) unsubscribe(); };
    }, [currentUser, profile, isApprovedWorker]);

    // ── Inactive / Unverified Worker View ──────────────────────────────────
    if (!loading && profile && !isApprovedWorker) {
        return (
            <div className="dashboard-page">
                <div className="inactive-worker-view">
                    <div className="container">
                        <div className="inactive-card">
                            <div className="inactive-icon">
                                <FaBriefcase />
                            </div>
                            <h1>Welcome, {currentUser?.displayName || 'Partner'}!</h1>
                            <p className="inactive-subtitle">
                                Your account is set up. To start receiving job requests, you need to apply as a service professional.
                            </p>

                            {profile.applicationPending ? (
                                <div className="pending-status">
                                    <FaHourglassHalf className="pending-icon" />
                                    <h3>Application Under Review</h3>
                                    <p>Your application has been submitted and is being reviewed by our admin team. You'll be notified once it's approved.</p>
                                </div>
                            ) : (
                                <Link to="/apply" className="btn btn-primary btn-lg become-partner-btn">
                                    <FaBriefcase /> Become a Partner
                                </Link>
                            )}

                            <div className="how-it-works-mini">
                                <h3>How It Works</h3>
                                <div className="steps-mini">
                                    <div className="step-mini">
                                        <div className="step-num">1</div>
                                        <p>Fill out the application form with your service details</p>
                                    </div>
                                    <div className="step-mini">
                                        <div className="step-num">2</div>
                                        <p>Our admin team reviews and verifies your credentials</p>
                                    </div>
                                    <div className="step-mini">
                                        <div className="step-num">3</div>
                                        <p>Once approved, start receiving job requests!</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Verified Worker Dashboard ──────────────────────────────────────────
    const statCards = [
        { key: 'pending', label: 'Pending', value: stats.pending, icon: <FaClock />, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
        { key: 'accepted', label: 'Accepted', value: stats.accepted, icon: <FaCheckCircle />, color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)' },
        { key: 'inProgress', label: 'In Progress', value: stats.inProgress, icon: <FaHardHat />, color: '#1a1a1a', bg: 'rgba(0, 0, 0, 0.07)' },
        { key: 'completed', label: 'Completed', value: stats.completed, icon: <FaTrophy />, color: '#FFD700', bg: 'rgba(255, 215, 0, 0.1)' },
    ];

    const quickActions = [
        { to: '/job-requests', icon: <FaClipboardList />, label: 'Job Requests', desc: 'View all requests' },
        { to: '/earnings', icon: <FaChartLine />, label: 'Earnings', desc: 'Track your income' },
        { to: '/profile', icon: <FaUserCircle />, label: 'My Profile', desc: 'Edit your details' },
    ];

    return (
        <div className="dashboard-page">
            <DashboardHero />

            <section className="section stats-section">
                <div className="container">
                    <div className="stats-grid">
                        {statCards.map(s => (
                            <div key={s.key} className="stat-card" style={{ '--stat-color': s.color, '--stat-bg': s.bg }}>
                                <div className="stat-icon-wrap">
                                    {s.icon}
                                </div>
                                <div className="stat-info">
                                    <span className="stat-value">{s.value}</span>
                                    <span className="stat-label">{s.label}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="section">
                <div className="container">
                    <div className="section-header-inline">
                        <div>
                            <h2>Recent Requests</h2>
                            <p className="section-subtitle">Your latest job activity</p>
                        </div>
                        <Link to="/job-requests" className="view-all-link">View all <span>&rarr;</span></Link>
                    </div>

                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner"></div>
                            <p>Loading your dashboard...</p>
                        </div>
                    ) : recentJobs.length > 0 ? (
                        <div className="jobs-grid">
                            {recentJobs.map((job) => (
                                <JobRequestCard key={job.id} job={job} />
                            ))}
                        </div>
                    ) : (
                        <div className="empty-dashboard">
                            <div className="empty-illustration">
                                <span>&#128236;</span>
                            </div>
                            <h3>All caught up!</h3>
                            <p>No new job requests right now. We'll notify you when a customer books your service.</p>
                        </div>
                    )}
                </div>
            </section>

            <section className="section quick-actions-section">
                <div className="container">
                    <h3 className="qa-title">Quick Actions</h3>
                    <div className="actions-grid">
                        {quickActions.map((a, i) => (
                            <Link key={i} to={a.to} className="action-card">
                                <div className="action-icon-wrap">{a.icon}</div>
                                <div className="action-text">
                                    <span className="action-label">{a.label}</span>
                                    <span className="action-desc">{a.desc}</span>
                                </div>
                            </Link>
                        ))}
                        <button className="action-card" onClick={() => window.location.reload()}>
                            <div className="action-icon-wrap"><FaSyncAlt /></div>
                            <div className="action-text">
                                <span className="action-label">Refresh</span>
                                <span className="action-desc">Reload data</span>
                            </div>
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default DashboardPage;
